import React from 'react';
import styled from 'styled-components';
import { media, theme } from 'styles';
import { Element as ScrollElement, Link } from 'react-scroll';
import bannerImage from 'images/banners/who-we-are-banner.jpg';
import benefit01 from 'images/benefit-icons/MT_Benefit-01.png';
import benefit02 from 'images/benefit-icons/MT_Benefit-02.png';
import benefit03 from 'images/benefit-icons/MT_Benefit-03.png';
import benefit04 from 'images/benefit-icons/MT_Benefit-04.png';
import benefit05 from 'images/benefit-icons/MT_Benefit-05.png';
import benefit06 from 'images/benefit-icons/MT_Benefit-06.png';
import benefit07 from 'images/benefit-icons/MT_Benefit-07.png';
import benefit08 from 'images/benefit-icons/MT_Benefit-08.png';
import team from 'images/team.jpg';
import detail01 from 'images/who-we-are-detail-01.svg';
import detail02 from 'images/who-we-are-detail-02.svg';
import { Breadcrumbs, Button, Container, SeoTags } from 'components/shared';
import FacebookTags from '../components/shared/FacebookTags/FacebookTags';

const { sp, colors } = theme;

const Wrapper = styled.div`
  background-color: ${colors.white};
`;

const Grid = styled.div`
  ${media.phablet`
    display: flex;
    flex-wrap: wrap;
  `};
`;

const GridItem = styled.div`
  margin-bottom: 20px;
  background-color: ${colors.blueIce};
  padding: 60px 30px;
  margin-bottom: 40px;
  border: 1px solid rgba(43, 88, 153, 0.2);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  border-radius: 6px;
  text-align: center;
  min-height: 340px;

  &:nth-child(2n) {
    ${media.phablet`
      margin-right: 0;
    `};

    ${media.desktop`
      margin-right: 40px;
    `};
  }

  &:nth-child(4n) {
    ${media.desktop`
      margin-right: 0;
    `};
  }

  ${media.phablet`
    margin-right: 40px;
    width: calc((100% / 2) - (40px / 2));
  `};

  ${media.desktop`
    width: calc((100% / 4) - (120px / 4));
  `};
`;

const GridText = styled.div`
  font-size: 16px;
  line-height: 20px;
`;

const GridIcon = styled.div`
  display: inline-block;
  margin: 0 0 60px;
  width: 100px;
  height: 100px;
  border-radius: 50%;
  background-color: ${colors.white};

  img {
    max-width: 100%;
  }
`;

const Banner = styled.div`
  position: relative;
  background-image: url('${props => props.src}');
  background-size: cover;
  background-position: top right -260px;
  background-repeat: no-repeat;
  padding: 60px 0 100px;

  ${media.tablet`
    padding: 100px 0;
  `};

  h1 {
    color: ${colors.white};
    max-width: 600px;
    font-size: 32px;
    line-height: 40px;
    letter-spacing: -0.8px;
    margin-top: ${sp.l};
    margin-bottom: ${sp.xl};
    font-weight: 700;

    ${media.tablet`
      font-size: 60px;
      line-height: 72px;
      letter-spacing: -1.1px;
    `};
  }

  &::before {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(43, 88, 153, 0.7);
    content: '';
  }

  ${media.tablet`
    background-position: top right;
    min-height: 960px;

    &::before {
      background-color: rgba(43, 88, 153, 0.3);
    }
  `};
`;

const Wedge = styled.div`
  position: absolute;
  width: 110%;
  bottom: 0;
  left: -10%;

  &::after {
    position: absolute;
    left: 0;
    bottom: 0;
    width: 100%;
    height: 1px;
    background-color: ${colors.blueIce};
    content: '';
  }

  svg {
    display: block;
  }
`;

const Intro = styled.div`
  background-color: ${colors.blueIce};
  padding: 60px 0 100px;
  margin-bottom: 100px;

  ${media.tablet`
    padding: 100px 0 160px;
  `};

  h2 {
    margin: 0 0 20px;
    font-size: 36px;
    line-height: 43px;
  }
`;

const Benefits = styled.div`
  padding: 100px 0 160px;
  text-align: center;

  h2 {
    margin: 0 0 60px;
    font-size: 36px;
    line-height: 43px;
  }
`;

const Team = styled.div`
  position: relative;
  background-image: url('${props => props.src}');
  background-size: cover;
  background-position: center center;
  background-repeat: no-repeat;
  padding: 100px 0;
  min-height: 580px;

  &::before {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(43, 88, 153, 0.7);
    content: '';
  }
`;

const TeamWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
`;

const TeamContent = styled.div`
  ${media.tablet`
    width: 50%;
  `};

  h2 {
    margin: 0 0 20px;
    font-size: 36px;
    line-height: 43px;
    color: ${colors.white};
  }

  p {
    color: rgba(255, 255, 255, 0.7);
    margin: 0 0 30px;
  }
`;

const Detail = styled.div`
  padding: 0 0 100px;
`;

const DetailWrapper = styled.div`
  display: flex;
  flex-direction: column;

  ${media.tablet`
    flex-direction: row;
    justify-content: space-between;
  `};
`;

const DetailImage = styled.div`
  order: 1;
  text-align: center;

  img {
    max-width: 80%;
  }

  ${media.tablet`
    order: inherit;
    width: calc(50% - 50px);

    img {
      max-width: 100%;
    }
  `};
`;

const DetailContent = styled.div`
  order: 2;
  padding-top: 40px;

  h2 {
    margin: 0 0 20px;
    font-size: 36px;
    line-height: 43px;
  }

  p {
    margin: 0 0 30px;
  }

  ${media.tablet`
    order: inherit;
    width: calc(50% - 50px);
  `};
`;

function WhoWeAre() {
  const breadcrumbs = [
    {
      name: 'Home',
      link: '/',
    },
    {
      name: 'Who We Are',
      link: '/who-we-are',
    },
  ];

  return (
    <Wrapper>
      <SeoTags
        title="About MarketTap | MarketTap"
        description="MarketTap provides affordable and easy on demand marketing services when and as you need it. Our services help our clients sell more online. Learn more today."
      />

      <FacebookTags
        fbUrl={`${process.env.GATSBY_MARKETTAP_SITE_HOST}/who-we-are/`}
        fbTitle="About MarketTap | MarketTap"
        fbDescription="MarketTap provides affordable and easy on demand marketing services when and as you need it. Our services help our clients sell more online. Learn more today."
        fbImage={bannerImage}
      />

      <Banner src={bannerImage}>
        <Wedge>
          <svg viewBox="0 0 1400 100">
            <polygon points="0 0 1400 100 0 100" fill="#f7faff" />
          </svg>
        </Wedge>

        <Container>
          <Breadcrumbs breadcrumbs={breadcrumbs} left light />

          <h1>Our mission is to help our clients sell more online.</h1>
          <Link to="proactiveStrategy" smooth duration={1000}>
            <Button yellow scroll>
              Get Started
            </Button>
          </Link>
        </Container>
      </Banner>

      <ScrollElement name="proactiveStrategy">
        <Intro>
          <Container narrow>
            <h2>Proactive Strategy</h2>
            <p>
              Here at MarketTap, we define marketing success as the
              implementation of a proactive strategy that applies the right mix
              of marketing activities in order to get the results you want,
              ultimately achieving your business goals. With so many moving
              parts marketing isn’t easy and we know that, but it’s an important
              part of your business’ success.
            </p>
          </Container>
        </Intro>
      </ScrollElement>

      <Detail>
        <Container>
          <DetailWrapper>
            <DetailImage>
              <img src={detail01} alt="" />
            </DetailImage>
            <DetailContent>
              <h2>Seamless Interactions</h2>
              <p>
                We want to make your interactions with us completely seamless
                and efficient. That’s why we designed an online platform where
                you can easily buy and manage your marketing services. MarketTap
                was created to provide on-demand marketing support for
                small-to-medium businesses, and unlike traditional marketing
                agencies, with MarketTap there are no contracts or retainer
                fees!
              </p>
            </DetailContent>
          </DetailWrapper>
        </Container>
      </Detail>

      <Detail>
        <Container>
          <DetailWrapper>
            <DetailContent>
              <h2>Marketing your business just got a whole lot easier.</h2>
              <p>
                Whether you are looking for social media management,
                photography, content writing or a social media strategy, our
                pay-as-you-go model means you can simply buy the services you
                need, when you need them!
              </p>
              <Button to="/how-it-works" yellow>
                See how it works
              </Button>
            </DetailContent>
            <DetailImage>
              <img src={detail02} alt="" />
            </DetailImage>
          </DetailWrapper>
        </Container>
      </Detail>

      <Team src={team}>
        <Container>
          <TeamWrapper>
            <TeamContent>
              <h2>The team that makes it all possible</h2>
              <p>
                We are a passionate and diverse team of marketing superstars
                that understand the stresses that small businesses and
                e-commerce companies face.
              </p>
              <p>
                We work to create amazing marketing services that offer a return
                on investment that will benefit your business. Whether you’ve
                been in business for years or just starting out – know that our
                team of professionals is are in your corner and here to help you
                become the best that you can be.
              </p>
              <Button to="/services" yellow>
                Shop Services
              </Button>
            </TeamContent>
          </TeamWrapper>
        </Container>
      </Team>

      <Benefits>
        <Container>
          <h2>What makes us unique!</h2>
          <Grid>
            <GridItem>
              <GridIcon>
                <img src={benefit01} alt="" />
              </GridIcon>
              <GridText>
                A la carte marketing services - pick and choose what only you
                need.
              </GridText>
            </GridItem>

            <GridItem>
              <GridIcon>
                <img src={benefit02} alt="" />
              </GridIcon>
              <GridText>
                Ability to buy and manage services within our platform.
              </GridText>
            </GridItem>

            <GridItem>
              <GridIcon>
                <img src={benefit03} alt="" />
              </GridIcon>
              <GridText>Simple and affordable, pay-as-you-go pricing.</GridText>
            </GridItem>

            <GridItem>
              <GridIcon>
                <img src={benefit04} alt="" />
              </GridIcon>
              <GridText>Quick and transparent delivery timelines.</GridText>
            </GridItem>

            <GridItem>
              <GridIcon>
                <img src={benefit05} alt="" />
              </GridIcon>
              <GridText>No contracts or retainer fees.</GridText>
            </GridItem>

            <GridItem>
              <GridIcon>
                <img src={benefit06} alt="" />
              </GridIcon>
              <GridText>High-quality work delivered every time!</GridText>
            </GridItem>

            <GridItem>
              <GridIcon>
                <img src={benefit07} alt="" />
              </GridIcon>
              <GridText>Our team of experts, available on-demand.</GridText>
            </GridItem>

            <GridItem>
              <GridIcon>
                <img src={benefit08} alt="" />
              </GridIcon>
              <GridText>Exceptional client satisfaction.</GridText>
            </GridItem>
          </Grid>
        </Container>
      </Benefits>
    </Wrapper>
  );
}

export default WhoWeAre;



// WEBPACK FOOTER //
// ./src/pages/who-we-are.js